// Objective color-match metric for the "match a target look" workflow. Compares
// the current graded frame against a target reference image in perceptual CIE
// Lab and produces (a) a headline ΔE2000 score and (b) a compact, signed diff
// report the AI colorist can act on (global L/a/b bias, per-luminance-band ΔL,
// per-region ΔE). Signs are TARGET minus CURRENT — i.e. how CURRENT must move.

function srgbToLinear(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

function rgbToLab(r, g, b) {
  const R = srgbToLinear(r); const G = srgbToLinear(g); const B = srgbToLinear(b);
  let X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
  let Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(X); const fy = f(Y); const fz = f(Z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

// CIEDE2000 — the perceptual standard. Inputs are Lab triplets.
function deltaE2000(L1, a1, b1, L2, a2, b2) {
  const rad = Math.PI / 180;
  const C1 = Math.hypot(a1, b1); const C2 = Math.hypot(a2, b2);
  const Cb = (C1 + C2) / 2;
  const G = 0.5 * (1 - Math.sqrt(Cb ** 7 / (Cb ** 7 + 25 ** 7)));
  const a1p = (1 + G) * a1; const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1); const C2p = Math.hypot(a2p, b2);
  const h1p = (Math.atan2(b1, a1p) * 180) / Math.PI + (Math.atan2(b1, a1p) < 0 ? 360 : 0);
  const h2p = (Math.atan2(b2, a2p) * 180) / Math.PI + (Math.atan2(b2, a2p) < 0 ? 360 : 0);
  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360; else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * rad) / 2);
  const Lbp = (L1 + L2) / 2;
  const Cbp = (C1p + C2p) / 2;
  let hbp = h1p + h2p;
  if (C1p * C2p !== 0) {
    if (Math.abs(h1p - h2p) > 180) hbp += (h1p + h2p < 360) ? 360 : -360;
    hbp /= 2;
  }
  const T = 1 - 0.17 * Math.cos((hbp - 30) * rad) + 0.24 * Math.cos(2 * hbp * rad)
    + 0.32 * Math.cos((3 * hbp + 6) * rad) - 0.20 * Math.cos((4 * hbp - 63) * rad);
  const dTheta = 30 * Math.exp(-(((hbp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cbp ** 7 / (Cbp ** 7 + 25 ** 7));
  const Sl = 1 + (0.015 * (Lbp - 50) ** 2) / Math.sqrt(20 + (Lbp - 50) ** 2);
  const Sc = 1 + 0.045 * Cbp;
  const Sh = 1 + 0.015 * Cbp * T;
  const Rt = -Math.sin(2 * dTheta * rad) * Rc;
  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

// Sparse, fixed-position Lab samples of a drawable, SORTED by luminance — the
// basis of the alignment-robust metric (see diffReport). Fixed indices keep the
// measurement stable across rounds; sparse sampling makes it resilient to local
// artifacts and small framing errors.
const GW = 220; const GH = 124; const NSAMP = 6000;
let _seed = 0x9e3779b9;
function rnd() { _seed |= 0; _seed = (_seed + 0x6D2B79F5) | 0; let t = Math.imul(_seed ^ (_seed >>> 15), 1 | _seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }
const SAMPLE_IDX = Array.from({ length: NSAMP }, () => Math.floor(rnd() * GW * GH));
function labSamples(srcEl) {
  const oc = document.createElement('canvas');
  oc.width = GW; oc.height = GH;
  const ctx = oc.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(srcEl, 0, 0, GW, GH);
  const d = ctx.getImageData(0, 0, GW, GH).data;
  const arr = new Array(NSAMP);
  for (let k = 0; k < NSAMP; k += 1) { const i = SAMPLE_IDX[k] * 4; arr[k] = rgbToLab(d[i], d[i + 1], d[i + 2]); }
  arr.sort((p, q) => p[0] - q[0]);
  return arr;
}
const chroma = (p) => Math.hypot(p[1], p[2]);

export function loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('image load failed'));
    im.src = src;
  });
}

const s1 = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`;

// ---- Auto-align: find the crop of our frame that matches a reference's framing
// The reference is graded differently, so we match on STRUCTURE (gradient
// magnitude of luminance) via normalized cross-correlation — grade/exposure
// invariant. Returns a normalized {x,y,w,h} crop at the reference's aspect.

function dimsOf(el) {
  return {
    w: el.naturalWidth || el.videoWidth || el.width || 0,
    h: el.naturalHeight || el.videoHeight || el.height || 0,
  };
}

function lumaArray(el, w, h) {
  const oc = document.createElement('canvas');
  oc.width = w; oc.height = h;
  const ctx = oc.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(el, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const out = new Float32Array(w * h);
  for (let i = 0, p = 0; i < d.length; i += 4, p += 1) {
    out[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
  }
  return out;
}

function gradMag(luma, w, h) {
  const g = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      g[i] = Math.abs(luma[i + 1] - luma[i - 1]) + Math.abs(luma[i + w] - luma[i - w]);
    }
  }
  return g;
}

function zNorm(arr) {
  let m = 0;
  for (let i = 0; i < arr.length; i += 1) m += arr[i];
  m /= arr.length || 1;
  let s = 0;
  for (let i = 0; i < arr.length; i += 1) { const d = arr[i] - m; s += d * d; }
  s = Math.sqrt(s / (arr.length || 1)) || 1;
  const out = new Float32Array(arr.length);
  for (let i = 0; i < arr.length; i += 1) out[i] = (arr[i] - m) / s;
  return out;
}

function sampleRect(src, SW, SH, x0, y0, cw, ch, dw, dh) {
  const out = new Float32Array(dw * dh);
  for (let j = 0; j < dh; j += 1) {
    const sy = Math.min(SH - 1, Math.max(0, Math.floor(y0 + ((j + 0.5) / dh) * ch)));
    for (let i = 0; i < dw; i += 1) {
      const sx = Math.min(SW - 1, Math.max(0, Math.floor(x0 + ((i + 0.5) / dw) * cw)));
      out[j * dw + i] = src[sy * SW + sx];
    }
  }
  return out;
}

// Returns a normalized crop {x,y,w,h} of sourceEl whose content best matches the
// framing of refEl, or null. Both must be loaded drawables.
export function autoAlignCrop(sourceEl, refEl) {
  const sd = dimsOf(sourceEl); const rd = dimsOf(refEl);
  if (!sd.w || !sd.h || !rd.w || !rd.h) return null;
  const refAR = rd.w / rd.h;
  const srcAR = sd.w / sd.h;
  const RW = 96; const RH = Math.max(8, Math.round(RW / refAR));
  const refGrad = zNorm(gradMag(lumaArray(refEl, RW, RH), RW, RH));
  const SW = 260; const SH = Math.max(8, Math.round(SW / srcAR));
  const srcLuma = lumaArray(sourceEl, SW, SH);
  const cwMax = Math.min(SW, SH * refAR);

  const scoreAt = (cw, x0, y0) => {
    const ch = cw / refAR;
    const cx = Math.min(Math.max(0, x0), SW - cw);
    const cy = Math.min(Math.max(0, y0), SH - ch);
    const samp = sampleRect(srcLuma, SW, SH, cx, cy, cw, ch, RW, RH);
    const aN = zNorm(gradMag(samp, RW, RH));
    let dot = 0;
    for (let i = 0; i < aN.length; i += 1) dot += aN[i] * refGrad[i];
    return { score: dot / aN.length, x0: cx, y0: cy, cw, ch };
  };

  // Coarse pass: several scales × a position grid.
  let best = null;
  const N = 9;
  for (const f of [1.0, 0.9, 0.8, 0.7, 0.6, 0.5]) {
    const cw = cwMax * f; const ch = cw / refAR;
    for (let yi = 0; yi < N; yi += 1) {
      const y0 = (SH - ch) * (N === 1 ? 0 : yi / (N - 1));
      for (let xi = 0; xi < N; xi += 1) {
        const x0 = (SW - cw) * (N === 1 ? 0 : xi / (N - 1));
        const r = scoreAt(cw, x0, y0);
        if (!best || r.score > best.score) best = r;
      }
    }
  }
  if (!best) return null;

  // Fine refine: shrink the search window around the best twice.
  let stepX = (SW - best.cw) / (N - 1);
  let stepY = (SH - best.ch) / (N - 1);
  for (let pass = 0; pass < 3; pass += 1) {
    const cur = best;
    for (const cwf of [0.94, 1.0, 1.06]) {
      const cw = Math.min(cwMax, cur.cw * cwf);
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          const r = scoreAt(cw, cur.x0 + dx * stepX * 0.5, cur.y0 + dy * stepY * 0.5);
          if (r.score > best.score) best = r;
        }
      }
    }
    stepX *= 0.5; stepY *= 0.5;
  }

  return { x: best.x0 / SW, y: best.y0 / SH, w: best.cw / SW, h: best.ch / SH };
}

// Compare CURRENT (drawable) to TARGET (drawable) with an ALIGNMENT-ROBUST
// metric: sample a fixed sparse set of pixels from each, sort BOTH by luminance
// and pair by RANK — comparing the joint tone+color DISTRIBUTION (a luminance-
// ranked optimal-transport ΔE) rather than by pixel position, so a small framing
// error no longer poisons the score. Returns { dE, text, metrics } where metrics
// drives precise per-tone-band targeting. (regions kept for back-compat.)
export function diffReport(curEl, tgtEl) {
  const curArr = labSamples(curEl);
  const tgtArr = labSamples(tgtEl);
  const n = Math.min(curArr.length, tgtArr.length);
  let sumDE = 0; let sL = 0; let sa = 0; let sb = 0; let cC = 0; let tC = 0;
  const band = [0, 1, 2].map(() => ({ dL: 0, da: 0, db: 0, dc: 0, n: 0 }));
  for (let i = 0; i < n; i += 1) {
    const c = curArr[i]; const t = tgtArr[i];
    sumDE += deltaE2000(c[0], c[1], c[2], t[0], t[1], t[2]);
    sL += t[0] - c[0]; sa += t[1] - c[1]; sb += t[2] - c[2]; cC += chroma(c); tC += chroma(t);
    const bi = i < n / 3 ? 0 : i < (2 * n) / 3 ? 1 : 2;
    band[bi].dL += t[0] - c[0]; band[bi].da += t[1] - c[1]; band[bi].db += t[2] - c[2]; band[bi].dc += chroma(t) - chroma(c); band[bi].n += 1;
  }
  const dE = sumDE / n;
  const cL = curArr.map((p) => p[0]); const tL = tgtArr.map((p) => p[0]);
  const q = (a, x) => a[Math.min(a.length - 1, Math.floor(x * a.length))];
  const spreadCur = q(cL, 0.9) - q(cL, 0.1); const spreadTgt = q(tL, 0.9) - q(tL, 0.1);
  const bt = (lbl, b) => `  ${lbl}: ΔL ${s1(b.dL / b.n)}, Δa ${s1(b.da / b.n)}, Δb ${s1(b.db / b.n)}, Δchroma ${s1(b.dc / b.n)}`;
  const contrastCue = spreadTgt > spreadCur + 3 ? 'raise contrast' : spreadTgt < spreadCur - 3 ? 'lower contrast' : 'contrast ~ok';
  const satCue = tC / n > cC / n + 3 ? 'increase saturation/vibrance' : tC / n < cC / n - 3 ? 'reduce saturation' : 'saturation ~ok';
  const text = [
    `Alignment-robust ΔE2000 (luminance-rank paired distribution) = ${dE.toFixed(1)} (0 identical; <5 very close).`,
    `Global bias (TARGET − CURRENT): L ${s1(sL / n)}, a ${s1(sa / n)} (− green/teal, + magenta), b ${s1(sb / n)} (− blue, + yellow/warm).`,
    `Contrast: current P10–P90 L-spread ${spreadCur.toFixed(0)} vs target ${spreadTgt.toFixed(0)} → ${contrastCue}.`,
    `Saturation: mean chroma current ${(cC / n).toFixed(1)} vs target ${(tC / n).toFixed(1)} → ${satCue}.`,
    `By tone band (TARGET − CURRENT) — use color-grading split-tone + HSL:`,
    bt('shadows', band[0]), bt('midtones', band[1]), bt('highlights', band[2]),
    `(teal = Δa<0 & Δb<0; warm = Δb>0. Push shadow/mid chroma toward teal; warm the highlights.)`,
  ].join('\n');
  const metrics = {
    gL: sL / n, ga: sa / n, gb: sb / n,
    spreadCur, spreadTgt, chromaCur: cC / n, chromaTgt: tC / n,
    bands: band.map((b) => ({ dL: b.dL / b.n, da: b.da / b.n, db: b.db / b.n, dc: b.dc / b.n })),
  };
  return { dE, text, metrics };
}

// Deterministic band-guided targeted moves derived from a diffReport's metrics —
// the same coordinate-descent set the headless matcher uses, so the in-app loop
// does PRECISE per-band split-toning (teal into the bands that need it, warm
// where Δb>0) plus contrast/saturation/WB nudges, not just global moves. Each is
// line-searched by ΔE in the loop. Returns [{ tag, apply(grade)->grade }].
const clampR = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export function targetedMoves(metrics) {
  if (!metrics) return [];
  const m = metrics; const out = [];
  const nudge = (tag, path, add, lo = -100, hi = 100) => out.push({ tag, path, add, lo, hi });
  // global tone / contrast / exposure
  if (m.spreadTgt > m.spreadCur + 3) nudge('contrast+', ['basic', 'contrast'], 16);
  if (m.spreadTgt < m.spreadCur - 3) nudge('contrast-', ['basic', 'contrast'], -14);
  if (Math.abs(m.gL) > 4) nudge(`exposure ${m.gL > 0 ? '+' : '-'}`, ['basic', 'exposure'], m.gL > 0 ? 0.2 : -0.2, -5, 5);
  // per-band tone via blacks / whites / shadows / highlights
  const sL = m.bands[0].dL; const hL = m.bands[2].dL;
  if (sL < -3) { nudge('blacks-', ['basic', 'blacks'], -12); nudge('shadows-', ['basic', 'shadows'], -12); }
  if (sL > 3) { nudge('blacks+', ['basic', 'blacks'], 12); nudge('shadows+', ['basic', 'shadows'], 12); }
  if (hL > 3) { nudge('whites+', ['basic', 'whites'], 12); nudge('highlights+', ['basic', 'highlights'], 12); }
  if (hL < -3) { nudge('whites-', ['basic', 'whites'], -12); nudge('highlights-', ['basic', 'highlights'], -12); }
  // saturation / vibrance
  if (m.chromaTgt > m.chromaCur + 3) { nudge('vibrance+', ['presence', 'vibrance'], 18); nudge('saturation+', ['presence', 'saturation'], 12); }
  if (m.chromaTgt < m.chromaCur - 3) nudge('saturation-', ['presence', 'saturation'], -12);
  // white balance
  if (Math.abs(m.gb) > 4) nudge(`temp ${m.gb > 0 ? '+' : '-'}`, ['wb', 'temp'], m.gb > 0 ? 10 : -10);
  if (Math.abs(m.ga) > 3) nudge(`tint ${m.ga > 0 ? '+' : '-'}`, ['wb', 'tint'], m.ga > 0 ? 7 : -7);
  // finer per-band split-tone hue sweeps
  const regions = [['shadows', m.bands[0]], ['midtones', m.bands[1]], ['highlights', m.bands[2]]];
  for (const [reg, b] of regions) {
    if (b.dc > 2 && (b.da < -1 || b.db < -3)) {
      const s = Math.min(85, Math.max(15, Math.round(b.dc * 2.2)));
      for (const h of [176, 186, 196, 206, 216]) out.push({ tag: `cg.${reg} teal h${h}`, cg: { reg, h, s } });
    }
    if (b.db > 4) {
      const s = Math.min(60, Math.max(12, Math.round(b.db * 1.5)));
      for (const h of [35, 45, 55, 65]) out.push({ tag: `cg.${reg} warm h${h}`, cg: { reg, h, s } });
    }
  }
  // HSL water bands (Aqua=4, Blue=5): saturation, luminance, hue
  if (m.chromaTgt > m.chromaCur + 2) { nudge('aqua sat+', ['hsl', 4, 's'], 25); nudge('blue sat+', ['hsl', 5, 's'], 22); }
  nudge('aqua lum-', ['hsl', 4, 'l'], -14);
  nudge('blue lum-', ['hsl', 5, 'l'], -14);
  nudge('aqua hue+', ['hsl', 4, 'h'], 12);
  // one optional local mask for the bright teal wave-face (kept only if it helps)
  out.push({ tag: 'mask wave-face teal', mask: { id: 'mface', type: 'radial', cx: 0.56, cy: 0.5, rx: 0.42, ry: 0.46, feather: 0.6, invert: false, amount: 1, range: { enabled: true, lo: 0.3, hi: 0.96, feather: 0.2 }, adj: { exposure: 0, contrast: 0, saturation: 26, temp: -18, tint: 0, black: 0 } } });
  return out;
}

// Resolve a targetedMoves entry against a concrete grade → a new grade.
export function applyTargeted(grade, move, setPath) {
  if (move.cg) {
    const g = setPath(grade, ['colorGrading', move.cg.reg, 'h'], move.cg.h);
    return setPath(g, ['colorGrading', move.cg.reg, 's'], move.cg.s);
  }
  if (move.mask) return { ...grade, masks: [...(Array.isArray(grade.masks) ? grade.masks : []), move.mask] };
  const cur = move.path.reduce((o, k) => (o == null ? undefined : o[k]), grade) || 0;
  return setPath(grade, move.path, clampR(cur + move.add, move.lo, move.hi));
}
